import React, { useEffect, useState } from "react";
import styled from "styled-components";
import BusinessIcon from "@material-ui/icons/Business";
import SearchIcon from "@material-ui/icons/Search";
import moment from "moment";
import { ChevronDown, CircleX } from "@sonder/ui/source/icons";
import { Dropdown } from "@sonder/ui/source/styled/components/Dropdown";
import { Button } from "@sonder/ui/source/styled/fragments/Button";
import { CheckboxIcon } from "@sonder/ui/source/styled/fragments/CheckboxIcon";
import { CheckboxWithLabel } from "@sonder/ui/source/styled/fragments/CheckboxWithLabel";
import { GenericDropdownItem } from "@sonder/ui/source/styled/fragments/GenericDropdownItem";
import {
  FilterButtonList,
  FilterDropdownContent,
  FilterEmpty,
  FilterFooter,
  FilterGroupItem,
} from "@sonder/ui/source/styled/fragments/GenericFilters";
import { Input } from "@sonder/ui/source/styled/fragments/Input";
import { InputWithIcons } from "@sonder/ui/source/styled/fragments/InputWithIcons";
import { Label } from "@sonder/ui/source/styled/fragments/Label";
import { colors } from "@sonder/ui/source/styled/theme";
import { useStore as useAuthStore } from "auth/store";
import { useDebounce } from "core/hooks/useDebounce";
import {
  SearchForTaskSubjectsItem,
  SearchForTaskSubjectsItemBuilding,
  SearchForTaskSubjectsItemInquiry,
  SearchForTaskSubjectsItemSpace,
  SearchForTaskSubjectsItemUnit,
  useSearchForTaskSubjects,
} from "core/queries/SearchForTaskSubjects";
import { titlelizeCase } from "core/utils/titlelizeCase";
import { SubjectTypeConfig } from "tasks/hooks/useCreateTaskFormConfig";

const StyledGenericDropdownItem = styled(GenericDropdownItem)`
  box-sizing: border-box;
  height: inherit;
  padding: 0.25rem 0.5rem;
`;

const StyledDropdownItemContent = styled.div`
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  min-width: 0;
`;

const StyledDropdownImageWrapper = styled.div`
  align-items: center;
  display: flex;
  justify-content: center;
  height: 3.07rem;
  margin-right: 0.5rem;
  width: 3.07rem;
`;

const StyledDropdownItemImage = styled.img`
  height: 100%;
  width: 100%;
`;

const StyledText = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`;

const StyledTitle = styled(StyledText)`
  color: ${colors.black};
  line-height: 1.71rem;
`;

const StyledSubtitle = styled(StyledText)`
  color: ${colors.gray6};
  line-height: 1.429rem;
`;

const StyledFilterButtonList = styled(FilterButtonList)`
  max-width: inherit;
`;

const StyledError = styled.p`
  color: ${colors.redD};
`;

const StyledClearDropdownTrigger = styled(Button)`
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translate3d(0, -50%, 0);
`;

const StyledDropdownTriggerWrapper = styled.div`
  background: ${colors.white};
  position: relative;

  ${Button},
  ${GenericDropdownItem} {
    border: 1px solid ${colors.grayE};
    border-radius: 0.3rem;
  }

  ${StyledClearDropdownTrigger} {
    border: 0;
    border-radius: 50%;
  }
`;

interface TaskSubjectSearchItemSharedProps {
  item: SearchForTaskSubjectsItem;
  onClick?: () => void;
}

interface TaskSubjectSearchBuildingItemProps
  extends TaskSubjectSearchItemSharedProps {
  building: SearchForTaskSubjectsItemBuilding;
}

const TaskSubjectSearchBuildingItem = ({
  building,
  item,
  onClick,
}: TaskSubjectSearchBuildingItemProps) => {
  const { title } = item;
  const { fullAddress, unitCount } = building;
  return (
    <StyledGenericDropdownItem onClick={() => (onClick ? onClick() : null)}>
      <StyledDropdownItemContent>
        <StyledTitle>{title}</StyledTitle>
        <StyledSubtitle>{fullAddress}</StyledSubtitle>
        <StyledSubtitle>Total units: {unitCount}</StyledSubtitle>
      </StyledDropdownItemContent>
    </StyledGenericDropdownItem>
  );
};

interface TaskSubjectSearchSpaceItemProps
  extends TaskSubjectSearchItemSharedProps {
  space: SearchForTaskSubjectsItemSpace;
}

const TaskSubjectSearchSpaceItem = ({
  space,
  item,
  onClick,
}: TaskSubjectSearchSpaceItemProps) => {
  const { title } = item;
  const { id, type } = space;
  return (
    <StyledGenericDropdownItem onClick={() => (onClick ? onClick() : null)}>
      <StyledDropdownItemContent>
        <StyledTitle>{title}</StyledTitle>
        <StyledSubtitle>ID: {id}</StyledSubtitle>
        {type && <StyledSubtitle>Type: {titlelizeCase(type)}</StyledSubtitle>}
      </StyledDropdownItemContent>
    </StyledGenericDropdownItem>
  );
};

interface TaskSubjectSearchInquiryItemProps
  extends TaskSubjectSearchItemSharedProps {
  inquiry: SearchForTaskSubjectsItemInquiry;
}

const TaskSubjectSearchInquiryItem = ({
  inquiry,
  item,
  onClick,
}: TaskSubjectSearchInquiryItemProps) => {
  const { title } = item;
  const { checkInTime, checkOutTime, unit } = inquiry;
  const shortinternalRef = unit?.shortInternalRef;
  const stayDates = `${moment(checkInTime).format(
    "MMM Do @ hh:mm A",
  )} - ${moment(checkOutTime).format("MMM Do @ hh:mm A")}`;
  return (
    <StyledGenericDropdownItem onClick={() => (onClick ? onClick() : null)}>
      <StyledDropdownItemContent>
        <StyledTitle>{title}</StyledTitle>
        <StyledSubtitle>{shortinternalRef}</StyledSubtitle>
        <StyledSubtitle>{stayDates}</StyledSubtitle>
      </StyledDropdownItemContent>
    </StyledGenericDropdownItem>
  );
};

interface TaskSubjectSearchUnitItemProps
  extends TaskSubjectSearchItemSharedProps {
  unit: SearchForTaskSubjectsItemUnit;
}

const TaskSubjectSearchUnitItem = ({
  item,
  onClick,
  unit,
}: TaskSubjectSearchUnitItemProps) => {
  const { title, subtitle } = item;
  const { images } = unit;
  const avatarUri = images[0]?.url;
  return (
    <StyledGenericDropdownItem onClick={() => (onClick ? onClick() : null)}>
      <StyledDropdownImageWrapper>
        {avatarUri ? (
          <StyledDropdownItemImage src={avatarUri} />
        ) : (
          <BusinessIcon fontSize="large" />
        )}
      </StyledDropdownImageWrapper>
      <StyledDropdownItemContent>
        <StyledTitle>{title}</StyledTitle>
        {subtitle && <StyledSubtitle>{subtitle}</StyledSubtitle>}
      </StyledDropdownItemContent>
    </StyledGenericDropdownItem>
  );
};

interface TaskSubjectSearchItemProp extends TaskSubjectSearchItemSharedProps {
  item: SearchForTaskSubjectsItem;
}

const TaskSubjectSearchItem = ({
  item,
  onClick,
}: TaskSubjectSearchItemProp) => {
  const resource = item.resource;

  switch (resource.__typename) {
    case "Inquiry":
    case "LeaseInquiry":
      return (
        <TaskSubjectSearchInquiryItem
          inquiry={resource as SearchForTaskSubjectsItemInquiry}
          item={item}
          onClick={onClick}
        />
      );
    case "Unit":
      return (
        <TaskSubjectSearchUnitItem
          item={item}
          unit={resource as SearchForTaskSubjectsItemUnit}
          onClick={onClick}
        />
      );

    case "Building":
      return (
        <TaskSubjectSearchBuildingItem
          building={resource as SearchForTaskSubjectsItemBuilding}
          item={item}
          onClick={onClick}
        />
      );

    case "CufSpace":
      return (
        <TaskSubjectSearchSpaceItem
          item={item}
          space={resource as SearchForTaskSubjectsItemSpace}
          onClick={onClick}
        />
      );

    default:
      return null;
  }
};

interface TaskSubjectSearchProps {
  onChange?: (subject: SearchForTaskSubjectsItem | null) => void;
  subject: SearchForTaskSubjectsItem | null;
  subjectType: SubjectTypeConfig;
}

export const TaskSubjectSearch = ({
  onChange,
  subject,
  subjectType,
}: TaskSubjectSearchProps) => {
  const { state: authState } = useAuthStore();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchAllCities, setSearchAllCities] = useState(true);
  const { searchPlaceholder = "Search subjects...", resultType } = subjectType;
  const cityId = authState?.user?.cityId;
  // TODO: When we add City SubjectType, we should not show the canToggleSearchAllCities Button.
  const canToggleSearchAllCities = Boolean(cityId);

  const onClear = () => {
    setSearchTerm("");
    if (onChange) {
      onChange(null);
    }
  };

  // Set City if authState.user.cityId exists.
  useEffect(() => {
    if (cityId) {
      setSearchAllCities(false);
    }
  }, [cityId]);
  // Clear search term and selected when resultType changes.
  useEffect(() => {
    setSearchTerm("");
  }, [subjectType]);

  // Copied from Aloha:
  // https://github.com/Flatbook/archipelago/blob/main/react-native/aloha/app/containers/task-creation/TaskTaxonomySubjectSelectorContainer.tsx#L158-L182
  const checkInEnd = moment().startOf("day").add(7, "days").toISOString();
  const checkOutStart = moment()
    .startOf("day")
    .subtract(3, "months")
    .toISOString();

  const cityIds = (!searchAllCities && cityId ? [cityId] : []).filter(Boolean);
  const filterInput = {
    inquiry: {
      cityIds,
      checkInEnd,
      checkOutStart,
    },
    leaseInquiry: {
      cityIds,
      startDateEnd: checkInEnd,
      endDateStart: checkOutStart,
    },
    unit: {
      cityIds,
    },
    space: {
      cityIds,
    },
    building: {
      cityIds,
    },
  };

  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const isSearchTermEmpty = debouncedSearchTerm === "";
  const { data, loading, error } = useSearchForTaskSubjects({
    skip: isSearchTermEmpty,
    variables: {
      filterInput,
      searchTerm: debouncedSearchTerm,
      resultType,
    },
  });

  const items = data?.searchWithMetadata?.items || [];

  const isEmpty = items.length === 0;
  const isEmptyAndLoading = isEmpty && loading;
  const isEmptyAndSearched = !loading && isEmpty && !isSearchTermEmpty;

  return (
    <Dropdown
      trigger={
        subject ? (
          <StyledDropdownTriggerWrapper>
            <TaskSubjectSearchItem item={subject} />
            <StyledClearDropdownTrigger
              iconOnly
              onClick={e => {
                onClear();
                e.stopPropagation();
              }}
            >
              <CircleX />
            </StyledClearDropdownTrigger>
          </StyledDropdownTriggerWrapper>
        ) : (
          <Button fullWidth iconRight disabled={!resultType} type="button">
            {searchPlaceholder}
            <ChevronDown />
          </Button>
        )
      }
    >
      {({ isOpen: isPortalOpen, closePortal }) =>
        isPortalOpen && (
          <FilterDropdownContent align="left">
            <InputWithIcons childLeft childRight>
              <SearchIcon />
              <Input
                autoFocus
                placeholder={searchPlaceholder}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </InputWithIcons>

            {error && error.message && (
              <StyledError>There was an error: {error.message}</StyledError>
            )}
            {isEmptyAndLoading && <FilterEmpty>Searching...</FilterEmpty>}
            {isEmptyAndSearched && (
              <FilterEmpty>{`No Results for ${searchTerm}.`}</FilterEmpty>
            )}
            {!isEmpty && !isEmptyAndSearched && (
              <StyledFilterButtonList>
                {items.map(item => {
                  return (
                    <TaskSubjectSearchItem
                      item={item}
                      key={item.id}
                      onClick={() => {
                        if (onChange) {
                          onChange(item);
                        }
                        closePortal();
                      }}
                    />
                  );
                })}
              </StyledFilterButtonList>
            )}
            {canToggleSearchAllCities && (
              <FilterFooter>
                <FilterGroupItem
                  noBorder
                  type="button"
                  onClick={() => {
                    setSearchAllCities(!searchAllCities);
                  }}
                >
                  <CheckboxWithLabel title="Search all Cities">
                    <CheckboxIcon checked={searchAllCities} />
                    <Label>Search all Cities</Label>
                  </CheckboxWithLabel>
                </FilterGroupItem>
              </FilterFooter>
            )}
          </FilterDropdownContent>
        )
      }
    </Dropdown>
  );
};
